import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, API_BASE, getAccessToken } from "@/lib/api";
import Layout from "@/components/Layout";
import { T } from "@/lib/testIds";
import { toast } from "sonner";
import imageCompression from "browser-image-compression";
import { Users, BookOpen, ClipboardCheck, Trophy, Save, Download, Search, CheckCircle2, Clock, XCircle, Copy, Trash2, ShieldCheck, UserMinus, Upload, X, MessageCircle, Plus, Pencil, KeyRound, Mail, Phone, ScrollText, Signature, Star } from "lucide-react";
import { CourseManager } from "./AdminCourses";
import { AssignmentsPanel } from "@/components/AssignmentsPanel";
import { useAuth } from "@/context/AuthContext";

function formatLastActive(iso) {
  if (!iso) return "never";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function Admin() {
  const { isSuperAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [team, setTeam] = useState(null);
  const [teamSearch, setTeamSearch] = useState("");
  const [settings, setSettings] = useState({
    practice_name: "",
    logo_url: "",
    signatory_name: "",
    signatory_title: "",
    certificate_validity_days: 365,
    regulator: "",
  });
  const [saving, setSaving] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const logoInputRef = useRef(null);
  const [resetResult, setResetResult] = useState(null); // { name, email, temporary_password, role? }
  const [saSearch, setSaSearch] = useState("");
  const [saResults, setSaResults] = useState([]);
  const [saSearching, setSaSearching] = useState(false);
  const [auditLog, setAuditLog] = useState(null);
  const [auditFilter, setAuditFilter] = useState("");   // event_type filter
  const [auditLoading, setAuditLoading] = useState(false);
  const [signingOff, setSigningOff] = useState(null);   // progress id currently posting

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setCompressing(true);
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.2,        // ~200KB
        maxWidthOrHeight: 512, // logos don't need to be huge
        useWebWorker: true,
        fileType: file.type === "image/png" ? "image/png" : "image/jpeg",
      });
      const dataUrl = await imageCompression.getDataUrlFromFile(compressed);
      setSettings((s) => ({ ...s, logo_url: dataUrl }));
      const kb = Math.round(compressed.size / 1024);
      toast.success(`Logo compressed to ${kb} KB — click Save to apply`);
    } catch (err) {
      toast.error("Could not compress that image");
    } finally {
      setCompressing(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const removeLogo = () => setSettings((s) => ({ ...s, logo_url: "" }));

  useEffect(() => {
    (async () => {
      const results = await Promise.allSettled([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/attempts"),
        api.get("/practice/settings"),
        api.get("/admin/team"),
      ]);
      if (results[0].status === "fulfilled") setStats(results[0].value.data);
      if (results[1].status === "fulfilled") setUsers(results[1].value.data);
      if (results[2].status === "fulfilled") setAttempts(results[2].value.data);
      if (results[3].status === "fulfilled") setSettings(results[3].value.data);
      if (results[4].status === "fulfilled") setTeam(results[4].value.data);
    })();
  }, []);

  const refreshTeam = async () => {
    try {
      const { data } = await api.get("/admin/team");
      setTeam(data);
    } catch {
      /* silent */
    }
  };

  const downloadCsv = async () => {
    try {
      const bearer = getAccessToken();
      const res = await fetch(`${API_BASE}/admin/team.csv`, {
        credentials: "include",
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mata-team-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Could not download CSV");
    }
  };

  const filteredLearners = useMemo(() => {
    if (!team) return [];
    const q = teamSearch.trim().toLowerCase();
    if (!q) return team.learners;
    return team.learners.filter((l) =>
      (l.name || "").toLowerCase().includes(q) || (l.email || "").toLowerCase().includes(q)
    );
  }, [team, teamSearch]);

  const changeRole = async (userId, newRole) => {
    const label = newRole === "admin" ? "promote to Practice Manager" : "demote to learner";
    if (!window.confirm(`Are you sure you want to ${label}?`)) return;
    try {
      const { data } = await api.put(`/admin/users/${userId}/role`, { role: newRole });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: data.role } : u)));
      toast.success(newRole === "admin" ? "Promoted to Practice Manager" : "Demoted to learner");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update role");
    }
  };

  const deleteUser = async (userId, name) => {
    if (!window.confirm(`Delete ${name} and all their progress? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      // Also refresh team + stats
      const [teamRes, statsRes] = await Promise.allSettled([api.get("/admin/team"), api.get("/admin/stats")]);
      if (teamRes.status === "fulfilled") setTeam(teamRes.value.data);
      if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
      toast.success(`${name} removed`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete user");
    }
  };

  const resetLearnerPassword = async (userId, name) => {
    if (!window.confirm(`Reset password for ${name}? A new one-time password will be generated for you to share with them in person.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-password`, {});
      setResetResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reset password");
    }
  };

  const searchSuperAdmin = async (e) => {
    e?.preventDefault?.();
    const q = saSearch.trim();
    if (q.length < 3) {
      toast.error("Enter at least 3 characters of the user's email");
      return;
    }
    setSaSearching(true);
    try {
      const { data } = await api.get(`/superadmin/users/search`, { params: { email: q } });
      setSaResults(data.users || []);
      if (!data.users?.length) toast("No users match that email");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Search failed");
    } finally {
      setSaSearching(false);
    }
  };

  const resetSuperAdminUser = async (u) => {
    if (!window.confirm(`Reset password for ${u.name || u.email} (${u.role === "admin" ? "PM" : "learner"} at ${u.practice_name || "—"})?`)) return;
    try {
      const { data } = await api.post(`/superadmin/users/${u.id}/reset-password`, {});
      setResetResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password");
    }
  };

  const copyTempPassword = async () => {
    if (!resetResult?.temporary_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.temporary_password);
      toast.success("Password copied to clipboard");
    } catch {
      toast.error("Copy failed — please write it down");
    }
  };

  const signOffCourse = async (progressId, learnerName, courseTitle) => {
    if (!progressId) return;
    if (!window.confirm(`Sign off ${learnerName} for "${courseTitle}"? This is recorded in the audit log and cannot be undone.`)) return;
    setSigningOff(progressId);
    try {
      await api.post(`/admin/progress/${progressId}/sign-off`);
      // Refresh the team snapshot so the sign-off timestamp appears
      const teamRes = await api.get("/admin/team");
      setTeam(teamRes.data);
      toast.success(`${learnerName} signed off for ${courseTitle}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not sign off");
    } finally {
      setSigningOff(null);
    }
  };

  const loadAuditLog = async (typeFilter = auditFilter) => {
    setAuditLoading(true);
    try {
      const params = typeFilter ? `?event_type=${encodeURIComponent(typeFilter)}` : "";
      const url = isSuperAdmin ? `/superadmin/audit-log${params}` : `/admin/audit-log${params}`;
      const { data } = await api.get(url);
      setAuditLog(data);
    } catch (e) {
      toast.error("Could not load audit log");
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    // Lazy-load audit log on mount so the PM dashboard opens fast, then it hydrates
    loadAuditLog("");
  }, [isSuperAdmin]);

  const saveSettings = async (e) => {    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/practice/settings", {
        practice_name: settings.practice_name || "Calside",
        logo_url: settings.logo_url || "",
        signatory_name: settings.signatory_name || "Grant Cameron Anthony",
        signatory_title: settings.signatory_title || "Creator, Calside",
        certificate_validity_days: parseInt(settings.certificate_validity_days || 365, 10),
        regulator: "",
      });
      setSettings(data);
      toast.success("Practice settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const cards = stats ? [
    { icon: Users, label: "Users", value: stats.users },
    { icon: BookOpen, label: "Courses", value: stats.courses },
    { icon: ClipboardCheck, label: "Quiz attempts", value: stats.quiz_attempts },
    { icon: Trophy, label: "Quiz passes", value: stats.quiz_passes },
  ] : [];

  return (
    <Layout wide>
      <div className="text-xs uppercase tracking-[0.28em] text-brand-muted mb-2">
        Admin{settings?.practice_name && settings.practice_name !== "Calside" ? ` · ${settings.practice_name}` : ""}
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl text-brand-ink">Practice dashboard</h1>
      <p className="text-brand-body mt-2">Learner activity across Calside.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
        {cards.map(({ icon: Icon, label, value }) => (
          <div key={label} className="bg-white border border-brand-linen rounded-3xl p-6 shadow-card">
            <Icon className="w-5 h-5 text-brand-accent" strokeWidth={1.5} />
            <div className="text-xs uppercase tracking-widest text-brand-muted mt-4">{label}</div>
            <div className="font-serif text-4xl text-brand-ink mt-1">{value}</div>
          </div>
        ))}
      </div>

      {/* Quick actions — one-tap jumps to the PM's most-used tools so they
          don't have to scroll through the whole page hunting for them. */}
      <div className="mt-8 flex flex-wrap gap-3" data-testid="admin-quick-actions">
        <a
          href="#assign-courses"
          data-testid="admin-quick-assign"
          onClick={(e) => {
            const el = document.getElementById("assign-courses");
            if (el) {
              e.preventDefault();
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }}
          className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-5 py-2.5 text-sm hover:bg-brand-inkHover"
        >
          <ClipboardCheck className="w-4 h-4" strokeWidth={1.7} /> Assign a course
        </a>
        <Link
          to="/admin/guide"
          data-testid="admin-quick-guide"
          className="inline-flex items-center gap-2 border-2 border-brand-secondary/50 text-brand-ink rounded-full px-5 py-2.5 text-sm hover:bg-brand-secondary/10"
        >
          <BookOpen className="w-4 h-4 text-brand-secondary" strokeWidth={1.7} /> PM guide
        </Link>
        <a
          href="#team-dashboard"
          data-testid="admin-quick-team"
          onClick={(e) => {
            const el = document.getElementById("team-dashboard");
            if (el) {
              e.preventDefault();
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }}
          className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-5 py-2.5 text-sm hover:bg-brand-bone"
        >
          <Users className="w-4 h-4" strokeWidth={1.7} /> View team dashboard
        </a>
        <a
          href="#invite-code"
          data-testid="admin-quick-invite"
          onClick={(e) => {
            const el = document.getElementById("invite-code");
            if (el) {
              e.preventDefault();
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }}
          className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-5 py-2.5 text-sm hover:bg-brand-bone"
        >
          <Copy className="w-4 h-4" strokeWidth={1.7} /> Invite your team
        </a>
      </div>

      {/* PM support — direct line to Grant for password resets & other help */}
      <div data-testid="pm-support-card" className="mt-8 bg-brand-ink text-brand-bone rounded-3xl shadow-card p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.28em] text-brand-bone/60">PM support</div>
          <h2 className="font-serif text-2xl sm:text-3xl mt-2">Locked out or need a hand?</h2>
          <p className="text-sm text-brand-bone/80 mt-2 max-w-xl">
            Practice Managers can reset their own learners&rsquo; passwords below. If <em>you</em> get
            locked out, message Grant directly and he&rsquo;ll reset your PM password within the day.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:min-w-[260px]">
          <a href="mailto:Grant.Anthony2@nhs.scot"
            data-testid="pm-support-email"
            className="inline-flex items-center gap-3 px-4 py-3 rounded-full bg-brand-bone text-brand-ink hover:bg-white transition-colors">
            <Mail className="w-4 h-4" strokeWidth={1.8} />
            <span className="font-medium truncate">Grant.Anthony2@nhs.scot</span>
          </a>
          <a href="https://wa.me/447590335390" target="_blank" rel="noreferrer"
            data-testid="pm-support-whatsapp"
            className="inline-flex items-center gap-3 px-4 py-3 rounded-full border border-brand-bone/30 hover:bg-white/10 transition-colors">
            <Phone className="w-4 h-4" strokeWidth={1.8} />
            <span className="font-medium">WhatsApp · 07590 335390</span>
          </a>
        </div>
      </div>

      {/* Practice settings — for certificate branding */}
      <div className="mt-12 bg-white border border-brand-linen rounded-3xl shadow-card overflow-hidden">
        <div className="p-6 border-b border-brand-linen">
          <h2 className="font-serif text-2xl text-brand-ink">Practice settings</h2>
          <p className="text-sm text-brand-muted mt-1">Update your practice name, logo and signatory. These appear on every course certificate and on the public verify page.</p>
        </div>
        <form onSubmit={saveSettings} className="p-6 grid md:grid-cols-2 gap-5">
          <div>
            <label className="text-xs uppercase tracking-widest text-brand-muted">Practice name</label>
            <input data-testid={T.adminPracticeName} value={settings.practice_name || ""}
              onChange={(e) => setSettings({ ...settings, practice_name: e.target.value })}
              placeholder="Riverside Medical Centre"
              className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-brand-muted">Practice logo (optional)</label>
            <div className="mt-2 flex items-start gap-3 flex-wrap">
              {settings.logo_url ? (
                <div className="relative">
                  <img src={settings.logo_url} alt="Logo preview"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="h-20 w-20 object-contain rounded-xl bg-brand-bone border border-brand-linen p-2" />
                  <button type="button" onClick={removeLogo}
                    className="absolute -top-2 -right-2 p-1 bg-brand-accent text-white rounded-full hover:bg-brand-accentHover" title="Remove logo">
                    <X className="w-3 h-3" strokeWidth={2.5} />
                  </button>
                </div>
              ) : (
                <div className="h-20 w-20 rounded-xl bg-brand-bone border border-dashed border-brand-linen flex items-center justify-center text-brand-muted text-[10px] text-center px-2">No logo</div>
              )}
              <div className="flex-1 min-w-[200px]">
                <input ref={logoInputRef} type="file" accept="image/*" onChange={onLogoFile}
                  data-testid={T.adminPracticeLogo}
                  className="hidden" id="logo-file-input" />
                <button type="button" onClick={() => logoInputRef.current?.click()} disabled={compressing}
                  className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-4 py-2 hover:bg-brand-linen disabled:opacity-60">
                  <Upload className="w-4 h-4" strokeWidth={1.5} />
                  {compressing ? "Compressing…" : settings.logo_url ? "Replace logo" : "Upload logo"}
                </button>
                <div className="text-[11px] text-brand-muted mt-2">
                  Any image; automatically compressed to ~200 KB / 512 px. PNG or JPG.
                </div>
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-brand-muted">Signatory name</label>
            <input data-testid={T.adminSignatoryName} value={settings.signatory_name || ""}
              onChange={(e) => setSettings({ ...settings, signatory_name: e.target.value })}
              placeholder="Dr Sarah Patel"
              className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-brand-muted">Signatory title</label>
            <input data-testid={T.adminSignatoryTitle} value={settings.signatory_title || ""}
              onChange={(e) => setSettings({ ...settings, signatory_title: e.target.value })}
              placeholder="GP Partner / Practice Manager"
              className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-brand-muted">Certificate validity (days)</label>
            <input data-testid={T.adminValidityDays} type="number" min="0" max="3650" value={settings.certificate_validity_days ?? 365}
              onChange={(e) => setSettings({ ...settings, certificate_validity_days: e.target.value })}
              className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors" />
            <div className="text-[11px] text-brand-muted mt-1">365 = 1 year. Use 0 for certificates that never expire. Applies to newly issued certificates.</div>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button data-testid={T.adminSaveSettings} type="submit" disabled={saving}
              className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-6 py-3 hover:bg-brand-inkHover disabled:opacity-60">
              <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </form>
      </div>

      {/* Invite code + housekeeping */}
      <div id="invite-code" className="scroll-mt-24 mt-8 grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-brand-linen rounded-3xl shadow-card p-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-brand-accent" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl text-brand-ink">Invite code</h2>
          </div>
          <p className="text-sm text-brand-muted mt-1">Share this with your staff so they can join your practice on the Register page.</p>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <code data-testid={T.adminInviteCode} className="flex-1 min-w-[180px] text-lg font-mono tracking-widest text-brand-ink bg-brand-bone border border-brand-linen rounded-xl px-4 py-3 select-all">
              {settings.invite_code === undefined ? "Loading…" : (settings.invite_code || "—")}
            </code>
            <button
              type="button"
              data-testid={T.adminInviteCopy}
              onClick={() => {
                if (!settings.invite_code) return;
                navigator.clipboard.writeText(settings.invite_code);
                toast.success("Invite code copied");
              }}
              className="p-3 border border-brand-linen rounded-full hover:bg-brand-linen"
              title="Copy code"
            >
              <Copy className="w-4 h-4 text-brand-ink" strokeWidth={1.7} />
            </button>
            <button
              type="button"
              data-testid="admin-invite-whatsapp"
              onClick={() => {
                if (!settings.invite_code) return;
                const msg = `Hi! Join our team on Calside — the training portal for ${settings.practice_name || "our practice"}.\n\n1) Go to ${window.location.origin}/register\n2) Choose "I have an invite code"\n3) Enter this code: ${settings.invite_code}\n\nYour progress and certificates will show up on my dashboard.`;
                const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
              className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-4 py-2.5 hover:brightness-95 text-sm"
              title="Share invite via WhatsApp"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2} /> WhatsApp
            </button>
            <button
              type="button"
              data-testid={T.adminInviteRegen}
              onClick={async () => {
                if (!window.confirm("Regenerate the invite code? The old code will stop working immediately.")) return;
                try {
                  const { data } = await api.post("/practice/invite/regen");
                  setSettings((s) => ({ ...s, invite_code: data.invite_code }));
                  toast.success("New invite code generated");
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not regenerate");
                }
              }}
              className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-4 py-2.5 hover:bg-brand-linen text-sm"
            >
              Regenerate
            </button>
          </div>
          <div className="text-[11px] text-brand-muted mt-2 italic">Regenerating invalidates the previous code. Existing accounts are not affected.</div>
        </div>

        <div className="bg-white border border-brand-linen rounded-3xl shadow-card p-6">
          <div className="flex items-center gap-2">
            <UserMinus className="w-5 h-5 text-brand-accent" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl text-brand-ink">Housekeeping</h2>
          </div>
          <p className="text-sm text-brand-muted mt-1">
            Delete any learner in your practice whose email begins with <code className="font-mono text-brand-ink">test</code>, <code className="font-mono text-brand-ink">learner</code> or <code className="font-mono text-brand-ink">demo</code>.
            Admins are never removed.
          </p>
          <button
            type="button"
            data-testid={T.adminWipeTestUsers}
            onClick={async () => {
              if (!window.confirm("Delete every learner whose email starts with test/learner/demo? This cannot be undone.")) return;
              try {
                const { data } = await api.post("/admin/users/wipe-test");
                const [teamRes, statsRes, usersRes] = await Promise.allSettled([
                  api.get("/admin/team"), api.get("/admin/stats"), api.get("/admin/users"),
                ]);
                if (teamRes.status === "fulfilled") setTeam(teamRes.value.data);
                if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
                if (usersRes.status === "fulfilled") setUsers(usersRes.value.data);
                if (data.removed === 0) {
                  toast.success("No test learners found — you're clean");
                } else {
                  toast.success(`Removed ${data.removed} test learner${data.removed === 1 ? "" : "s"}`);
                }
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not wipe test users");
              }
            }}
            className="mt-4 inline-flex items-center gap-2 border border-brand-accent text-brand-accent rounded-full px-5 py-2.5 hover:bg-brand-accent hover:text-white transition-colors"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.7} /> Wipe test users
          </button>
        </div>
      </div>

      {/* Course assignments — PM assigns courses to specific learners (moved to
          top so PMs don't have to scroll past the whole Admin page to find it) */}
      {team && (
        <div id="assign-courses" className="scroll-mt-24">
          <AssignmentsPanel
            learners={team.learners}
            courses={team.courses}
            onChanged={refreshTeam}
          />
        </div>
      )}

      {/* Course authoring — CRUD for practice-scoped courses */}
      {isSuperAdmin && (
        <div className="mt-8 -mb-2 flex justify-end">
          <Link
            to="/super/tags"
            data-testid="admin-bulk-tags-shortcut"
            className="inline-flex items-center gap-2 border border-brand-accent/40 text-brand-ink rounded-full px-4 py-2 text-sm hover:bg-brand-linen transition-colors"
          >
            Bulk tag editor →
          </Link>
        </div>
      )}
      <CourseManager practiceName={settings?.practice_name} />

      {/* Team dashboard — matrix of learners × courses */}
      {team && (
        <div id="team-dashboard" className="scroll-mt-24 mt-12 bg-white border border-brand-linen rounded-3xl shadow-card overflow-hidden">
          <div className="p-6 border-b border-brand-linen flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="font-serif text-2xl text-brand-ink">Team dashboard</h2>
              <p className="text-sm text-brand-muted mt-1">Every learner's progress across every course. Download the CSV for CPD portfolios and training records.</p>
            </div>
            <div className="flex items-center gap-2 relative">
              <Search className="w-4 h-4 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
              <input data-testid={T.adminTeamSearch} value={teamSearch} onChange={(e) => setTeamSearch(e.target.value)}
                placeholder="Search learners…"
                className="pl-9 pr-4 py-2.5 border border-brand-linen rounded-full bg-brand-bone focus:outline-none focus:border-brand-ink text-sm" />
            </div>
            <button data-testid={T.adminTeamCsv} onClick={downloadCsv}
              className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-5 py-2.5 hover:bg-brand-inkHover">
              <Download className="w-4 h-4" strokeWidth={1.5} /> Download CSV
            </button>
          </div>

          <div className="overflow-x-auto" data-testid={T.adminTeamTable}>
            <table className="min-w-full text-sm">
              <thead className="bg-brand-bone border-b border-brand-linen">
                <tr>
                  <th className="text-left px-6 py-3 font-medium text-brand-muted text-[11px] uppercase tracking-widest">Learner</th>
                  {team.courses.map((c) => (
                    <th key={c.slug} className="text-left px-4 py-3 font-medium text-brand-muted text-[11px] uppercase tracking-widest min-w-[220px]">
                      {c.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredLearners.map((l) => (
                  <tr key={l.id} className="border-b border-brand-linen last:border-0 hover:bg-brand-bone/50">
                    <td className="px-6 py-4 align-top">
                      <div className="font-medium text-brand-ink">{l.name}</div>
                      <div className="text-xs text-brand-muted">{l.email}</div>
                      <div className="text-[10px] uppercase tracking-widest text-brand-muted mt-1">
                        Last active · {formatLastActive(l.last_login_at)}
                      </div>
                    </td>
                    {team.courses.map((c) => {
                      const p = l.progress[c.slug] || {};
                      const a = (l.assignments && l.assignments[c.slug]) || null;
                      const dueDate = a?.due_at ? new Date(a.due_at) : null;
                      const isOverdue = dueDate && !p.quiz_passed && dueDate.getTime() < Date.now();
                      const started = (p.percent || 0) > 0 || p.quiz_passed;
                      let icon, label, tone;
                      if (p.quiz_passed && p.is_expired) {
                        icon = <XCircle className="w-4 h-4 text-brand-accent" strokeWidth={1.7} />;
                        label = "Expired"; tone = "text-brand-accent";
                      } else if (p.quiz_passed && p.is_overdue) {
                        icon = <XCircle className="w-4 h-4 text-brand-accent" strokeWidth={1.7} />;
                        label = "Refresh due"; tone = "text-brand-accent";
                      } else if (p.quiz_passed) {
                        icon = <CheckCircle2 className="w-4 h-4 text-brand-ink" strokeWidth={1.7} />;
                        label = "Passed"; tone = "text-brand-ink";
                      } else if (started) {
                        icon = <Clock className="w-4 h-4 text-brand-secondary" strokeWidth={1.7} />;
                        label = "In progress"; tone = "text-brand-secondary";
                      } else {
                        icon = <span className="w-4 h-4 inline-block rounded-full border border-brand-linen" />;
                        label = "Not started"; tone = "text-brand-muted";
                      }
                      return (
                        <td key={c.slug} className="px-4 py-4 align-top">
                          <div className={`flex items-center gap-2 ${tone}`}>
                            {icon}
                            <span className="text-sm">{label}</span>
                            {a && !p.quiz_passed && (
                              <span
                                data-testid={`assigned-badge-${l.id}-${c.slug}`}
                                title={`Assigned by ${a.assigned_by_name || "PM"}${dueDate ? ` · due ${dueDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}${a.notes ? `\n\n${a.notes}` : ""}`}
                                className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${isOverdue ? "border-brand-accent text-brand-accent bg-brand-accent/5" : "border-brand-secondary text-brand-secondary bg-brand-secondary/5"}`}
                              >
                                {isOverdue ? "Overdue" : "Assigned"}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 flex items-center gap-3 text-xs text-brand-muted">
                            <span>{p.percent || 0}%</span>
                            {p.quiz_score !== undefined && p.quiz_score !== null && <span>· score {p.quiz_score}%</span>}
                          </div>
                          <div className="mt-1.5 h-1 bg-brand-linen rounded-full overflow-hidden">
                            <div className={`h-full ${p.quiz_passed ? "bg-brand-ink" : "bg-brand-secondary"} transition-all`} style={{ width: `${p.percent || 0}%` }} />
                          </div>
                          {p.refresh_due_at && p.quiz_passed && !p.is_expired && (
                            <div className={`text-[10px] mt-1.5 ${p.is_overdue ? "text-brand-accent font-medium" : "text-brand-muted"}`}>
                              {p.is_overdue
                                ? `Refresh overdue ${Math.abs(p.days_until_due)}d`
                                : `Refresh due ${new Date(p.refresh_due_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
                            </div>
                          )}
                          {p.certificate_expires_at && (
                            <div className="text-[10px] text-brand-muted mt-1.5">
                              Expires {new Date(p.certificate_expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                            </div>
                          )}
                          {p.quiz_passed && (
                            <div className="mt-2 pt-2 border-t border-brand-linen space-y-1.5">
                              {p.confidence ? (
                                <div className="flex items-center gap-1 text-[10px] text-brand-secondary" data-testid={`confidence-cell-${l.id}-${c.slug}`}>
                                  <Star className="w-3 h-3" strokeWidth={1.7} />
                                  Confidence {p.confidence}/5
                                </div>
                              ) : (
                                <div className="text-[10px] text-brand-muted italic">Confidence not rated</div>
                              )}
                              {p.signed_off_at ? (
                                <div
                                  className="flex items-center gap-1 text-[10px] text-brand-ink font-medium"
                                  title={`Signed off by ${p.signed_off_by_name || "PM"} on ${new Date(p.signed_off_at).toLocaleString("en-GB")}`}
                                  data-testid={`signed-off-${l.id}-${c.slug}`}
                                >
                                  <Signature className="w-3 h-3" strokeWidth={1.7} />
                                  Signed off · {new Date(p.signed_off_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                                </div>
                              ) : (
                                <button
                                  onClick={() => signOffCourse(p.id, l.name, c.title)}
                                  disabled={signingOff === p.id}
                                  data-testid={`sign-off-${l.id}-${c.slug}`}
                                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-brand-ink border border-brand-ink rounded-full px-2.5 py-1 hover:bg-brand-ink hover:text-brand-bone disabled:opacity-60"
                                >
                                  <Signature className="w-3 h-3" strokeWidth={1.7} />
                                  {signingOff === p.id ? "Signing…" : "Sign off"}
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {filteredLearners.length === 0 && (
                  <tr>
                    <td colSpan={team.courses.length + 1} className="px-6 py-8 text-center text-brand-muted text-sm">
                      No learners match your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}


      <div className="mt-12 grid lg:grid-cols-2 gap-8">
        <div className="bg-white border border-brand-linen rounded-3xl shadow-card overflow-hidden">
          <div className="p-6 border-b border-brand-linen flex items-center justify-between">
            <h2 className="font-serif text-2xl text-brand-ink">Learners</h2>
            <span className="text-xs uppercase tracking-widest text-brand-muted">{users.length} total</span>
          </div>
          <div data-testid={T.adminUsersTable} className="max-h-[420px] overflow-y-auto">
            {users.map((u) => (
              <div key={u.id} className="px-6 py-4 border-b border-brand-linen last:border-0 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-brand-ink font-medium truncate">{u.name}</div>
                  <div className="text-sm text-brand-muted truncate">{u.email}</div>
                  <div className="text-[11px] uppercase tracking-widest text-brand-muted mt-1">
                    Last active · <span data-testid={`last-active-${u.id}`}>{formatLastActive(u.last_login_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[11px] uppercase tracking-widest px-3 py-1 rounded-full ${u.role === "admin" ? "bg-brand-ink text-brand-bone" : "bg-brand-linen text-brand-secondary"}`}>
                    {u.role === "admin" ? "PM" : "Learner"}
                  </span>
                  {u.role === "admin" ? (
                    <button data-testid={`demote-${u.id}`} onClick={() => changeRole(u.id, "learner")}
                      className="p-2 border border-brand-linen rounded-full hover:bg-brand-accent hover:text-white hover:border-brand-accent" title="Demote to learner">
                      <UserMinus className="w-3.5 h-3.5" strokeWidth={1.7} />
                    </button>
                  ) : (
                    <button data-testid={`promote-${u.id}`} onClick={() => changeRole(u.id, "admin")}
                      className="p-2 border border-brand-linen rounded-full hover:bg-brand-ink hover:text-brand-bone hover:border-brand-ink" title="Promote to Practice Manager">
                      <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.7} />
                    </button>
                  )}
                  {u.role !== "admin" && (
                    <button data-testid={`reset-password-${u.id}`} onClick={() => resetLearnerPassword(u.id, u.name)}
                      className="p-2 border border-brand-linen rounded-full hover:bg-brand-ink hover:text-brand-bone hover:border-brand-ink" title="Reset password">
                      <KeyRound className="w-3.5 h-3.5" strokeWidth={1.7} />
                    </button>
                  )}
                  <button data-testid={`delete-user-${u.id}`} onClick={() => deleteUser(u.id, u.name)}
                    className="p-2 border border-brand-linen rounded-full hover:bg-brand-accent hover:text-white hover:border-brand-accent" title="Delete user">
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.7} />
                  </button>
                </div>
              </div>
            ))}
            {users.length === 0 && <div className="p-6 text-brand-muted text-sm">No users yet.</div>}
          </div>
        </div>

        <div className="bg-white border border-brand-linen rounded-3xl shadow-card overflow-hidden">
          <div className="p-6 border-b border-brand-linen flex items-center justify-between">
            <h2 className="font-serif text-2xl text-brand-ink">Recent quiz attempts</h2>
            <span className="text-xs uppercase tracking-widest text-brand-muted">{attempts.length}</span>
          </div>
          <div data-testid={T.adminAttemptsTable} className="max-h-[420px] overflow-y-auto">
            {attempts.map((a) => (
              <div key={a.id} className="px-6 py-4 border-b border-brand-linen last:border-0 flex items-center justify-between">
                <div>
                  <div className="text-brand-ink font-medium">{a.user.name}</div>
                  <div className="text-sm text-brand-muted">{a.user.email} · {a.course_slug}</div>
                </div>
                <div className="text-right">
                  <div className={`font-serif text-2xl ${a.passed ? "text-brand-ink" : "text-brand-accent"}`}>{a.score}%</div>
                  <div className="text-[11px] uppercase tracking-widest text-brand-muted">{a.passed ? "passed" : "not passed"}</div>
                </div>
              </div>
            ))}
            {attempts.length === 0 && <div className="p-6 text-brand-muted text-sm">No attempts yet.</div>}
          </div>
        </div>
      </div>

      {/* Super admin: cross-practice password reset */}
      {isSuperAdmin && (
        <div data-testid="superadmin-reset-section" className="mt-12 bg-white border border-brand-accent/40 rounded-3xl shadow-card overflow-hidden">
          <div className="p-6 border-b border-brand-linen">
            <div className="text-xs uppercase tracking-[0.28em] text-brand-accent">Super admin</div>
            <h2 className="font-serif text-2xl text-brand-ink mt-1">Reset a PM (or any user) password</h2>
            <p className="text-sm text-brand-muted mt-1">Search across every practice by email. Use this when a Practice Manager contacts you locked out.</p>
          </div>
          <form onSubmit={searchSuperAdmin} className="p-6 flex flex-col sm:flex-row gap-3">
            <input
              value={saSearch}
              onChange={(e) => setSaSearch(e.target.value)}
              placeholder="Search by email (min 3 chars)"
              data-testid="superadmin-search-input"
              className="flex-1 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors"
            />
            <button type="submit" disabled={saSearching}
              data-testid="superadmin-search-button"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-ink text-brand-bone hover:bg-brand-inkHover disabled:opacity-60">
              <Search className="w-4 h-4" strokeWidth={1.7} />
              {saSearching ? "Searching…" : "Search"}
            </button>
          </form>
          {saResults.length > 0 && (
            <div className="border-t border-brand-linen">
              {saResults.map((u) => (
                <div key={u.id} className="px-6 py-4 border-b border-brand-linen last:border-0 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-brand-ink font-medium truncate">{u.name || "(no name)"} · <span className="text-brand-muted">{u.email}</span></div>
                    <div className="text-xs text-brand-muted mt-1">
                      {u.role === "admin" ? "PM" : "Learner"} · {u.practice_name || "no practice"}
                    </div>
                  </div>
                  <button onClick={() => resetSuperAdminUser(u)}
                    data-testid={`superadmin-reset-${u.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-linen hover:bg-brand-ink hover:text-brand-bone hover:border-brand-ink transition-colors">
                    <KeyRound className="w-3.5 h-3.5" strokeWidth={1.7} />
                    Reset password
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Audit log — append-only trail of who did what and when */}
      <div data-testid="audit-log-section" className="mt-12 bg-white border border-brand-linen rounded-3xl shadow-card overflow-hidden">
        <div className="p-6 border-b border-brand-linen flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <ScrollText className="w-4 h-4 text-brand-secondary" strokeWidth={1.7} />
              <h2 className="font-serif text-2xl text-brand-ink">Audit log</h2>
            </div>
            <p className="text-sm text-brand-muted mt-1">
              {isSuperAdmin
                ? "Every event across every practice. Append-only — used for governance and disputes."
                : "Every important action for your practice — sign-offs, password resets, role changes, quiz passes. Append-only."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={auditFilter}
              onChange={(e) => { setAuditFilter(e.target.value); loadAuditLog(e.target.value); }}
              data-testid="audit-filter"
              className="px-4 py-2.5 border border-brand-linen rounded-full bg-brand-bone text-sm focus:outline-none focus:border-brand-ink"
            >
              <option value="">All events</option>
              <option value="pm_sign_off">PM sign-off</option>
              <option value="quiz_pass">Quiz pass</option>
              <option value="quiz_fail">Quiz fail</option>
              <option value="confidence_submitted">Confidence rating</option>
              <option value="login">Login</option>
              <option value="password_reset_pm">Password reset (PM)</option>
              <option value="password_reset_super">Password reset (super admin)</option>
              <option value="role_change">Role change</option>
              <option value="user_delete">User deleted</option>
            </select>
            <button
              onClick={() => loadAuditLog(auditFilter)}
              data-testid="audit-refresh"
              className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-4 py-2.5 hover:bg-brand-linen text-sm"
            >
              {auditLoading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>
        <div className="max-h-[520px] overflow-y-auto" data-testid="audit-log-list">
          {(!auditLog || auditLog.rows?.length === 0) ? (
            <div className="p-6 text-brand-muted text-sm">
              {auditLoading ? "Loading audit log…" : "No events recorded yet. Try a sign-off or a role change."}
            </div>
          ) : (
            auditLog.rows.map((row) => {
              const eventLabel = {
                pm_sign_off: "PM sign-off",
                quiz_pass: "Quiz passed",
                quiz_fail: "Quiz failed",
                confidence_submitted: "Confidence rated",
                login: "Signed in",
                password_reset_pm: "Password reset (PM)",
                password_reset_super: "Password reset (super)",
                role_change: "Role changed",
                user_delete: "User deleted",
              }[row.event_type] || row.event_type;
              const isSignOff = row.event_type === "pm_sign_off";
              const tone = isSignOff
                ? "border-brand-ink/40 bg-brand-ink/5"
                : row.event_type === "quiz_fail" || row.event_type === "user_delete"
                ? "border-brand-accent/40"
                : "border-brand-linen";
              const when = row.created_at ? new Date(row.created_at) : null;
              const meta = row.metadata || {};
              const subjectLabel = row.subject?.name || row.subject?.email || "—";
              const actorLabel = row.actor?.name || row.actor?.email || "system";
              return (
                <div
                  key={row.id}
                  data-testid={`audit-row-${row.id}`}
                  className={`px-6 py-4 border-b last:border-0 ${tone}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] uppercase tracking-widest text-brand-muted">
                        {when ? when.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </div>
                      <div className="text-brand-ink font-medium mt-1">{eventLabel}</div>
                      <div className="text-sm text-brand-secondary mt-1">
                        <span className="text-brand-ink">{actorLabel}</span>
                        {row.subject?.id && row.subject?.id !== row.actor?.id && (
                          <> · about <span className="text-brand-ink">{subjectLabel}</span></>
                        )}
                        {row.course_slug && <> · course <em>{row.course_slug}</em></>}
                      </div>
                      {(meta.score !== undefined || meta.confidence !== undefined || meta.target_email || meta.from) && (
                        <div className="text-xs text-brand-muted mt-1">
                          {meta.score !== undefined && <>Score: {meta.score}% · </>}
                          {meta.confidence !== undefined && <>Confidence: {meta.confidence}/5 · </>}
                          {meta.from && meta.to && <>{meta.from} → {meta.to} · </>}
                          {meta.target_email && <>{meta.target_email}</>}
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-muted whitespace-nowrap">
                      {row.actor?.role || ""}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {auditLog && auditLog.rows?.length > 0 && (
          <div className="px-6 py-3 border-t border-brand-linen text-[11px] uppercase tracking-widest text-brand-muted bg-brand-bone">
            Showing {auditLog.rows.length} of most recent {auditLog.limit}
          </div>
        )}
      </div>

      {/* One-time password reveal modal */}
      {resetResult && (
        <div className="fixed inset-0 z-50 bg-brand-ink/70 flex items-center justify-center p-4" data-testid="reset-result-modal">
          <div className="bg-white rounded-3xl shadow-card max-w-md w-full overflow-hidden">
            <div className="p-6 border-b border-brand-linen flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.28em] text-brand-accent">Password reset</div>
                <h3 className="font-serif text-2xl text-brand-ink mt-1">Share this with {resetResult.name || resetResult.email}</h3>
                <p className="text-sm text-brand-muted mt-1">
                  This is the <strong>only time</strong> the new password will be shown. Copy or write it down now.
                </p>
              </div>
              <button onClick={() => setResetResult(null)} className="p-2 rounded-full hover:bg-brand-linen" title="Close">
                <X className="w-4 h-4" strokeWidth={1.8} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-muted">Email</div>
                <div className="text-brand-ink font-medium break-all mt-1">{resetResult.email}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-muted">Temporary password</div>
                <div className="mt-2 flex items-center gap-2">
                  <code data-testid="reset-temp-password" className="flex-1 px-4 py-3 bg-brand-bone border border-brand-linen rounded-xl font-mono text-lg text-brand-ink tracking-wider break-all">
                    {resetResult.temporary_password}
                  </code>
                  <button onClick={copyTempPassword}
                    data-testid="reset-copy-password"
                    className="p-3 border border-brand-linen rounded-xl hover:bg-brand-ink hover:text-brand-bone hover:border-brand-ink" title="Copy">
                    <Copy className="w-4 h-4" strokeWidth={1.7} />
                  </button>
                </div>
              </div>
              <p className="text-xs text-brand-muted">
                Ask them to sign in with this password and change it straight away from their profile.
              </p>
              <button onClick={() => setResetResult(null)}
                data-testid="reset-close-button"
                className="w-full px-5 py-3 rounded-full bg-brand-ink text-brand-bone hover:bg-brand-inkHover">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
